import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { apiErr } from "../lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { History, Loader2, FileSpreadsheet, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { RowEditor, HeaderRow, BulkAssignSite } from "./AiImportDialog";
import { ImportConfirmDialog } from "./ImportConfirmDialog";

// Recover held/skipped rows of a committed run and force-include them WITHOUT
// re-uploading the file. Reuses the review RowEditor and the Confirm & Reconcile gate.
export function ImportRecoverDialog({ open, onClose, run, onDone }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [factors, setFactors] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open || !run) return;
    setLoading(true); setConfirmOpen(false);
    api.get(`/esg-import/${run.import_id}/staging`).then(({ data }) => {
      const held = (data.rows || [])
        .filter((r) => !r.committed)
        .map((r) => ({ ...r, __sheet: r.sheet_name || null, include: false }));
      setRows(held);
    }).catch((e) => { toast.error(apiErr(e)); onClose(); }).finally(() => setLoading(false));
    api.get("/emissions/factors").then((r) => setFactors(r.data.factors)).catch(() => {});
    api.get("/facilities").then((r) => setFacilities((r.data.facilities || []).map((f) => f.name).filter(Boolean))).catch(() => setFacilities([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, run]);

  const factorMap = useMemo(() => Object.fromEntries(factors.map((f) => [f.code, f])), [factors]);
  const update = (sheet, idx, k, v) => setRows((p) => p.map((r) => (r.__sheet === sheet && r.idx === idx ? { ...r, [k]: v } : r)));
  const convert = (sheet, idx) => setRows((p) => p.map((r) => (r.__sheet === sheet && r.idx === idx
    ? { ...r, unit: r.suggested_unit || r.unit, value: r.suggested_value ?? r.value, blocked: false, blocked_convertible: false, include: true } : r)));

  const grouped = useMemo(() => {
    const g = {};
    rows.forEach((r) => { const k = r.__sheet || "__single__"; (g[k] = g[k] || []).push(r); });
    return g;
  }, [rows]);

  const includeCount = rows.filter((r) => r.include && r.pillar && r.category).length;

  const forceInclude = async (ackMap = {}) => {
    setBusy(true);
    try {
      const payload = rows
        .filter((r) => r.include && r.pillar && r.category)
        .map((r) => ({
          idx: r.idx, sheet_name: r.__sheet || null, include: true,
          pillar: r.pillar, category: r.category, activity_type: r.activity_type,
          value: r.value === "" ? null : Number(r.value), unit: r.unit, period: r.period,
          scope: r.scope ?? null, factor_code: r.factor_code, facility: r.facility || null,
          override_ack: ackMap[r.__sheet ? `${r.__sheet}::${r.idx}` : String(r.idx)] || null,
        }));
      const { data } = await api.post(`/esg-import/${run.import_id}/force-include`, { rows: payload, origin: "measured" });
      setConfirmOpen(false);
      toast.success(`Force-included ${data.committed} record${data.committed === 1 ? "" : "s"} as Unverified`);
      onDone && onDone();
      onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="w-[96vw] max-w-[1200px] max-h-[90vh] overflow-y-auto" data-testid="import-recover-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><History className="h-5 w-5 text-accent" /> Recover held rows — {run?.filename}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-14 text-center"><Loader2 className="h-8 w-8 animate-spin text-accent mx-auto" /><p className="text-sm text-muted-foreground mt-2">Loading held rows…</p></div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground" data-testid="recover-empty">Every analysed row in this run has already been committed — nothing left to recover.</div>
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">These rows were analysed but not committed in this run. Tick the ones to add now — correct units, assign a factor/site or apply a safe conversion first. They import as <span className="text-[#FFB800] font-medium">Unverified</span>, no re-upload needed.</p>
            {Object.entries(grouped).map(([sheet, rs]) => (
              <div key={sheet} className="card-flat p-3 space-y-2" data-testid={`recover-sheet-${sheet}`}>
                {sheet !== "__single__" && <div className="inline-flex items-center gap-2 text-sm font-medium"><FileSpreadsheet className="h-4 w-4 text-accent" /> {sheet}</div>}
                {rs.some((r) => r.include && r.pillar && r.category && !r.facility) && facilities.length > 0 && (
                  <BulkAssignSite facilities={facilities} unassignedCount={rs.filter((r) => r.include && r.pillar && r.category && !r.facility).length}
                    onAssign={(fname) => setRows((p) => p.map((r) => (r.__sheet === (sheet === "__single__" ? null : sheet) && r.include && r.pillar && r.category && !r.facility ? { ...r, facility: fname } : r)))} />
                )}
                <div className="overflow-x-auto">
                  <table className="w-full"><thead><HeaderRow /></thead>
                    <tbody>{rs.map((r) => <RowEditor key={`${sheet}-${r.idx}`} r={r} factors={factors} factorMap={factorMap}
                      facilities={facilities} onChange={(idx, k, v) => update(sheet === "__single__" ? null : sheet, idx, k, v)}
                      onConvert={(idx) => convert(sheet === "__single__" ? null : sheet, idx)} />)}</tbody>
                  </table>
                </div>
              </div>
            ))}
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button variant="primary" onClick={() => setConfirmOpen(true)} disabled={busy || includeCount === 0} data-testid="recover-forceinclude-btn">
                Review &amp; force-include {includeCount} <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        <ImportConfirmDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          rows={rows}
          totalRows={rows.length}
          committing={busy}
          onConfirm={forceInclude}
        />
      </DialogContent>
    </Dialog>
  );
}
